/**
 * App-Geruest: Hash-Router und Rahmen (Kopfleiste, Inhalt, Tableiste).
 *
 * Hash-Routing statt History-API, weil die App spaeter unter Capacitor
 * ueber file:// laeuft – dort funktionieren Pfad-Routen nicht.
 */
import { h } from './ui/dom.ts';
import { icon } from './ui/icons.ts';
import { armBookClose, bookSlot, runBookOpen } from './ui/book-transition.ts';
import { createDrawingSurface } from './features/drawing/surface.ts';
import type { DrawingSurface } from './features/drawing/surface.ts';
import { getDrawing, saveDrawing } from './db/repo.ts';
import { cachedProject } from './db/active.ts';
import { can, onAccessChange } from './features/auth/session.ts';
import { setThemeId } from './db/repo.ts';
import { renderLibrary } from './views/library.ts';
import { renderReleases } from './views/releases.ts';
import { renderReleaseDetail } from './views/release-detail.ts';
import { renderCoverEditor } from './views/cover-edit.ts';
export { themeControls } from './theme/controls.ts';
import { setThemeSaver } from './theme/controls.ts';
import { renderPrompter } from './views/prompter.ts';
import { renderSongEditor } from './views/song-edit.ts';
import { renderSheetView } from './views/sheet.ts';
import { renderSetlists } from './views/setlists.ts';
import { renderBible } from './views/bible.ts';
import { renderBibleRoom } from './views/bible-room.ts';
import { renderBibleBoard } from './views/bible-board.ts';
import { renderProjects } from './views/projects.ts';
import { renderSetlistDetail } from './views/setlist-detail.ts';
import { renderStage } from './views/stage.ts';
import { renderSettings } from './views/settings.ts';
import { notFoundPage } from './features/error-page.ts';
import { appUrl } from './platform/endpoints.ts';

// Lead Sheets speichert das Design in den eigenen Einstellungen.
setThemeSaver((themeId) => void setThemeId(themeId));

/** Was eine View an den Rahmen zurueckgibt. */
export interface View {
  title: string;
  subtitle?: string;
  /** Ziel des Zurueck-Knopfes als Hash, z. B. "#/". */
  back?: string;
  /** Knoepfe rechts in der Kopfleiste. */
  actions?: HTMLElement[];
  body: HTMLElement;
  /** true = ohne Kopf- und Tableiste (Buehnen-Modus). */
  fullscreen?: boolean;
  /** true = ohne Standard-Innenabstand (Sheet-Ansicht). */
  flush?: boolean;
  /** Aufraeumen beim Verlassen der View (Timer, Listener). */
  cleanup?: () => void;
  /**
   * Eigene Zeichenflaeche der View. Ohne diese Angabe gibt es auf diesem
   * Bildschirm kein Zeichnen (und keinen Stift-Knopf).
   */
  drawing?: {
    /** Schluessel in der Datenbank. */
    id: string;
    /** Scrollbereich. */
    host: HTMLElement;
    /** Inhalt darin (liefert Breite und Anker). */
    content: HTMLElement;
    getAnchors?: () => HTMLElement[];
    getUnit?: () => number;
    /** Nur auf dem Inhalt zeichnen (siehe SurfaceOptions.clipToContent). */
    clipToContent?: boolean;
    /** Darf der Stift gerade zeichnen? */
    canDraw?: () => boolean;
  } | null;
}

export type ViewFactory = (params: Record<string, string>) => Promise<View> | View;

interface Route {
  /** z. B. "/song/:id" */
  pattern: string;
  factory: ViewFactory;
}

const ROUTES: Route[] = [
  // Songs beginnen jetzt bei den Veroeffentlichungen; die Liste aller Songs
  // liegt eine Ebene tiefer unter /songs.
  { pattern: '/', factory: renderReleases },
  { pattern: '/songs', factory: renderLibrary },
  { pattern: '/release/:id', factory: renderReleaseDetail },
  { pattern: '/projects', factory: renderProjects },
  { pattern: '/cover/:id', factory: renderCoverEditor },
  { pattern: '/song/:id', factory: renderSongEditor },
  { pattern: '/sheet/:id', factory: renderSheetView },
  { pattern: '/setlists', factory: renderSetlists },
  { pattern: '/setlist/:id', factory: renderSetlistDetail },
  { pattern: '/bible', factory: renderBible },
  { pattern: '/bible/room/:id', factory: renderBibleRoom },
  { pattern: '/bible/board/:id', factory: renderBibleBoard },
  { pattern: '/stage/song/:id', factory: renderStage },
  { pattern: '/stage/setlist/:id/:index', factory: renderStage },
  { pattern: '/prompter', factory: renderPrompter },
  { pattern: '/prompter/:id', factory: renderPrompter },
  { pattern: '/settings', factory: renderSettings },
  { pattern: '/settings/:section', factory: renderSettings },
  { pattern: '/settings/:section/:doc', factory: renderSettings },
  // Die Verwaltung (Admin) liegt in System – eigene Seite, eigene Adresse.
  // Alte Links auf #/admin fuehren dorthin weiter.
  { pattern: '/admin', factory: toSystem },
  { pattern: '/admin/:section', factory: toSystem },
];

/** Einträge der Seitenleiste. */
const NAV = [
  {
    hash: '#/',
    icon: 'note',
    label: 'Songs',
    match: (p: string) =>
      p === '/' || p.startsWith('/song') || p.startsWith('/sheet') || p.startsWith('/release'),
  },
  { hash: '#/setlists', icon: 'list', label: 'Setlists', match: (p: string) => p.startsWith('/setlist') },
  {
    hash: '#/bible',
    icon: 'book',
    label: 'Band-Bibel',
    match: (p: string) => p.startsWith('/bible'),
  },
  { hash: '#/prompter', icon: 'mic', label: 'Teleprompter', match: (p: string) => p.startsWith('/prompter') },
  { hash: '#/settings', icon: 'gear', label: 'Einstellungen', match: (p: string) => p.startsWith('/settings') },
];

const SIDEBAR_KEY = 'leadsheet:sidebar';

/** Offen oder zu? Auf breiten Bildschirmen ist offen die sinnvolle Vorgabe. */
function initialSidebar(): 'open' | 'closed' {
  try {
    const stored = localStorage.getItem(SIDEBAR_KEY);
    if (stored === 'open' || stored === 'closed') return stored;
  } catch {
    /* gesperrter Speicher ist kein Fehlerfall */
  }
  return window.innerWidth >= 900 ? 'open' : 'closed';
}

export function setSidebar(next: 'open' | 'closed'): void {
  document.body.dataset.sidebar = next;
  try {
    localStorage.setItem(SIDEBAR_KEY, next);
  } catch {
    /* egal */
  }
}

export function toggleSidebar(): void {
  setSidebar(document.body.dataset.sidebar === 'open' ? 'closed' : 'open');
}

/** Baut die Seitenleiste für die aktuelle Ansicht. */
function buildSidebar(path: string, projectName: string | undefined): HTMLElement {
  const items = NAV.map((item) =>
    h(
      'button',
      {
        class: 'sidebar__item' + (item.match(path) ? ' sidebar__item--on' : ''),
        attrs: { type: 'button', 'aria-current': item.match(path) ? 'page' : null },
        on: { click: () => navigate(item.hash) },
      },
      h('span', { class: 'sidebar__icon' }, icon(item.icon)),
      h('span', { class: 'sidebar__label', text: item.label }),
    ),
  );

  return h(
    'aside',
    { class: 'sidebar', attrs: { 'aria-label': 'Navigation' } },
    h(
      'div',
      { class: 'sidebar__head' },
      h(
        'button',
        {
          class: 'sidebar__toggle',
          attrs: { type: 'button', 'aria-label': 'Navigation schließen', title: 'Navigation schließen' },
          on: { click: () => toggleSidebar() },
        },
        icon('panel'),
      ),
    ),
    h(
      'button',
      {
        class: 'sidebar__project',
        attrs: { type: 'button' },
        // Der Weg zurück ins Regal: dieselbe Bewegung wie beim Öffnen, nur
        // rückwärts. armBookClose legt sofort eine deckende Seite darüber,
        // die neue Ansicht führt sie dann zu Ende (ui/book-transition.ts).
        on: {
          click: () => {
            armBookClose();
            navigate('#/projects');
          },
        },
      },
      h('span', { class: 'sidebar__project-label', text: 'Projekt' }),
      h('span', { class: 'sidebar__project-name', text: projectName || 'Projekte' }),
    ),
    h('nav', { class: 'sidebar__nav' }, ...items, adminItem(path)),
    srItem(),
  );
}

/**
 * Schnittstelle Rot – das Dach ueber Lead Sheets (Uebersicht mit Cloud,
 * Build und System). Adresse aus platform/endpoints.ts.
 */
function srItem(): HTMLElement {
  return h(
    'a',
    { class: 'sidebar__item sidebar__sr', attrs: { href: appUrl('home'), title: 'Schnittstelle Rot – Cloud, Build und System' } },
    h('span', { class: 'sidebar__icon' }, h('span', { class: 'sidebar__sr-mark' })),
    h('span', { class: 'sidebar__label', text: 'Schnittstelle Rot' }),
  );
}

/**
 * Der Eintrag in den Wartungsbereich.
 *
 * Er wird immer gebaut, aber erst eingeblendet, wenn die Rechte es hergeben –
 * gefragt wird nach der FAEHIGKEIT, nicht nach einer Rolle. So aendert sich
 * hier nichts, wenn spaeter eine echte Anmeldung dazukommt.
 * Der eigentliche Schutz sitzt in der Ansicht selbst, nicht an diesem Knopf.
 */
function adminItem(path: string): HTMLElement {
  const el = h(
    'button',
    {
      class: 'sidebar__item' + (path.startsWith('/admin') ? ' sidebar__item--on' : ''),
      attrs: { type: 'button', hidden: 'hidden' },
      on: { click: () => location.assign(appUrl('system')) },
    },
    h('span', { class: 'sidebar__icon' }, icon('wrench')),
    h('span', { class: 'sidebar__label', text: 'Admin' }),
  );
  void can('admin.view').then((allowed) => {
    el.hidden = !allowed;
  });
  return el;
}

/**
 * Schwebender Admin-Knopf rechts unten, nur fuer Administratoren. Liegt ein
 * Plus-Knopf (.fab) an derselben Stelle, rueckt er darueber (base.css).
 */
function adminFab(): HTMLElement {
  const el = h(
    'button',
    {
      class: 'admin-fab',
      attrs: { type: 'button', hidden: 'hidden', title: 'Admin – Schnittstelle Rot · System', 'aria-label': 'Admin' },
      on: { click: () => location.assign(appUrl('system')) },
    },
    icon('wrench'),
  );
  void can('admin.view').then((allowed) => {
    el.hidden = !allowed;
  });
  return el;
}

/** #/admin[/bereich] → System (Verwaltung), gleicher Bereich. */
function toSystem(params: Record<string, string>): View {
  location.replace(appUrl('system', params.section ? `#/${params.section}` : ''));
  return { title: 'System', body: h('p', { class: 'login__loading', text: 'Weiter zu System …' }), drawing: null };
}

function currentPath(): string {
  const raw = location.hash.replace(/^#/, '');
  return raw === '' ? '/' : raw;
}

/** Vergleicht Pfad und Muster und liefert die Parameter. */
function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('/').filter(Boolean);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i += 1) {
    const seg = p[i] as string;
    const val = a[i] as string;
    if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(val);
    else if (seg !== val) return null;
  }
  return params;
}

export function navigate(hash: string, replace = false): void {
  if (replace) location.replace(hash);
  else location.hash = hash;
}

let cleanupCurrent: (() => void) | null = null;
let currentSurface: DrawingSurface | null = null;
let renderToken = 0;

interface Shell {
  element: HTMLElement;
  /** Der scrollbare Inhaltsbereich – Standardflaeche zum Zeichnen. */
  main: HTMLElement | null;
}

function buildShell(view: View): Shell {
  if (view.fullscreen) return { element: view.body, main: null };

  const path = currentPath();

  const topbar = h(
    'header',
    { class: 'topbar' },
    // Der Knopf klappt die Seitenleiste auf und zu.
    h(
      'button',
      {
        class: 'topbar__menu',
        attrs: { type: 'button', 'aria-label': 'Navigation', title: 'Navigation' },
        on: { click: () => toggleSidebar() },
      },
      icon('panel'),
    ),
    view.back
      ? h(
          'button',
          {
            class: 'btn btn--ghost btn--icon',
            attrs: { type: 'button', 'aria-label': 'Zurueck' },
            on: { click: () => navigate(view.back as string) },
          },
          icon('back'),
        )
      : null,
    h(
      'h1',
      { class: 'topbar__title' },
      document.createTextNode(view.title),
      view.subtitle ? h('span', { class: 'topbar__sub', text: view.subtitle }) : null,
    ),
    ...(view.actions ?? []),
  );

  const main = h('main', { class: 'view' + (view.flush ? ' view--flush' : '') }, view.body);

  const scrim = h('div', {
    class: 'sidebar-scrim',
    on: { click: () => setSidebar('closed') },
  });

  const element = h(
    'div',
    { class: 'app app--shell' },
    buildSidebar(path, cachedProject()?.name),
    scrim,
    h('div', { class: 'app__main' }, topbar, main),
    path.startsWith('/admin') ? null : adminFab(),
  );

  return { element, main };
}

/**
 * Haengt die Zeichenflaeche an die gerade gezeigte Ansicht.
 * Ohne eigene Angabe der View wird der Inhaltsbereich benutzt und unter dem
 * Pfad gespeichert – jeder Bildschirm hat damit seine eigene Ebene.
 */
async function mountDrawing(view: View): Promise<DrawingSurface | null> {
  // Gezeichnet wird nur, wo es Sinn ergibt: Lead Sheet (Ueben, Buehne),
  // Notizen der Band-Bibel und Einband. Diese Ansichten bringen ihre
  // Zeichenflaeche selbst mit; Listen, Einstellungen, Admin usw. nicht.
  const config = view.drawing;
  document.body.classList.toggle('no-drawing', !config);
  if (!config) return null;

  const doc = await getDrawing(config.id);
  return createDrawingSurface({
    host: config.host,
    content: config.content,
    getAnchors: config.getAnchors,
    getUnit: config.getUnit,
    clipToContent: config.clipToContent,
    canDraw: config.canDraw,
    doc,
    onChange: (next) => void saveDrawing(next),
  });
}

async function render(): Promise<void> {
  const token = (renderToken += 1);
  const path = currentPath();
  const root = document.getElementById('app');
  if (!root) return;

  cleanupCurrent?.();
  cleanupCurrent = null;
  currentSurface?.destroy();
  currentSurface = null;
  // Offene Dialoge gehoeren nicht zur naechsten Ansicht.
  for (const modal of document.querySelectorAll('.modal')) modal.remove();

  for (const route of ROUTES) {
    const params = match(route.pattern, path);
    if (!params) continue;
    let view: View;
    try {
      view = await route.factory(params);
    } catch (err) {
      console.error(err);
      view = {
        title: 'Fehler',
        back: '#/',
        body: h('div', { class: 'empty' }, h('p', { text: String(err) })),
      };
    }
    // Zwischendurch wurde erneut navigiert – dieses Ergebnis verwerfen.
    if (token !== renderToken) return;
    const shell = buildShell(view);
    // Auf dem Regal stören der Zeichenknopf und der Einrichtungshinweis,
    // dort gibt es nichts anzumerken und nichts zu installieren.
    document.body.classList.toggle('shelf-open', path === '/projects');
    /*
     * Wird gerade ein Buch aufgeschlagen, entsteht die Ansicht NICHT in #app,
     * sondern als Seite im Buch – verkleinert, aber echt und lebendig. Erst
     * wenn die Kamera angekommen ist, wandert genau dieser Knoten nach #app.
     * Solange bleibt das Regal in #app stehen und dient als Hintergrund.
     */
    const slot = bookSlot();
    const target = slot ?? root;
    target.textContent = '';
    target.append(shell.element);
    cleanupCurrent = view.cleanup ?? null;
    if (slot) {
      // Erst aufschlagen, dann messen: die Zeichenebene arbeitet mit
      // Bildschirmkoordinaten und bekaeme in der verkleinerten Seite falsche.
      await runBookOpen();
      if (token !== renderToken) return;
    }
    const surface = await mountDrawing(view);
    // Waehrend des Wartens kann erneut navigiert worden sein.
    if (token !== renderToken) surface?.destroy();
    else currentSurface = surface;
    return;
  }

  root.textContent = '';
  root.append(
    buildShell({
      title: 'Nicht gefunden',
      fullscreen: true,
      body: notFoundPage(() => navigate('#/projects')),
      drawing: null,
    }).element,
  );
}

/** Misst die Zeichenebene neu, z. B. nach einer Schriftgroessenaenderung. */
export function refreshDrawing(): void {
  currentSurface?.refresh();
}

/** Baut die aktuelle Ansicht neu auf (z. B. nach dem Loeschen eines Eintrags). */
export function refresh(): void {
  void render();
}

export function startRouter(): void {
  setSidebar(initialSidebar());
  // Der Buchuebergang meldet sich, wenn die Ansicht wieder in voller Groesse
  // an ihrem Platz steht – dann muss die Zeichenebene neu messen.
  window.addEventListener('leadsheet:layout', () => refreshDrawing());
  // Wird der Wartungsbereich ein- oder ausgeschaltet, muss die Navigation
  // das sofort zeigen.
  onAccessChange(() => void render());
  window.addEventListener('hashchange', () => {
    // Auf schmalen Bildschirmen liegt die Leiste über dem Inhalt und soll
    // nach der Auswahl zur Seite gehen.
    if (window.innerWidth < 900) setSidebar('closed');
    void render();
  });
  void render();
}
